
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import uuid from 'uuid';
import { Dictionary } from 'vue-router/types/router';

@Component({
	components: {},
})
export default class DataInfo extends Vue {
	@Prop() public readonly name!: string;

	public id = uuid.v4();

	public get info() {
		return this.dic[this.name] || 'No info';
	}

	public dic: Dictionary<string> = {
		// =====================================================================
		// TOGGLE DESCRIPTION STANDARDS (for reference)
		// =====================================================================
		// Use the following standardized format for toggle descriptions:
		//
		// <br><br>Optional: Include Credit Note, Debit Note.
		// <br><br>Optional: Include Credit Note, Debit Note, Tax.
		// <br><br>Optional: Include Credit Note, Debit Note.
		// <br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.
		//
		// Toggle options:
		// - Credit Note         → include credit note in calculation
		// - Debit Note          → include debit note in calculation
		// - Tax                 → include tax in calculation
		// - Unapplied           → include unapplied payments/credits not yet knocked off against invoices
		// - Show Profit         → show profit line in charts
		// - Toggle Profit/Margin → switch between profit amount and margin %
		// - Sales Module Only (SL) excludes direct Customer Module (AR) documents.
		// - Purchase Module Only (PH) excludes direct Supplier Module (AP) documents.
		//
		// Order: Credit Note → Debit Note → Tax → Unapplied → Show Profit → Module Only
		// =====================================================================

		// =====================================================================
		// GENERAL
		// =====================================================================
		coming_soon: 'Coming Soon',

		// =====================================================================
		// DASHBOARD
		// =====================================================================
		dashboard_sales_number_displayer:
			'Sales invoice amount and number of documents of selected date, compared to a 7-day average.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note, Tax.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		dashboard_estimate_profit_number_displayer:
			'Sales profit of selected date, compared to a 7-day average.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note, Tax.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		dashboard_quotation_number_displayer:
			'Amount and number of quotations issued of selected date, compared to a 7-day average.' +
			'<br><br><span class="info-optional">Optional: Include Tax.</span>',
		dashboard_sales_agent_ranking_table:
			'Daily sales agent ranking by billing amount of selected date.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note, Tax.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		dashboard_sales_customer_ranking_table:
			'Daily customer sales ranking by billing amount of selected date.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note, Tax.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		dashboard_current_month_sales_line_chart:
			'Sales and profit data within the current month up to selected date. Daily breakdown with summary table.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note, Tax, Show Profit.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		dashboard_current_month_quotation_line_chart:
			'Quotation and converted quotation within the month based on selected date. Daily breakdown with summary table.' +
			'<br><br><span class="info-optional">Optional: Include Tax.</span>',
		dashboard_cash_in_bank_displayer: 'Cash in bank amount as of selected date.',
		dashboard_cash_on_hand_displayer: 'Cash on hand amount as of selected date.',
		dashboard_net_cash_flow_number_displayer: 'Net cash flow amount based on selected date.',
		dashboard_cash_in_flow_number_displayer: 'Cash in amount based on selected date.',
		dashboard_cash_out_flow_number_displayer: 'Cash out amount based on selected date.',
		dashboard_customer_due_table: 'Customer documents due based on selected day.',
		dashboard_supplier_due_table: 'Supplier documents due based on selected day.',

		// =====================================================================
		// SALES - OVERVIEW
		// =====================================================================
		sales_number_displayer:
			'Sales amount based on selected date range. Number of customers who contributed to sales.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		sales_profit_number_displayer:
			'Sales profit or margin based on selected date range.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note, Toggle Profit/Margin.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		sales_12_months_sales_number_displayer:
			'Year to date sales amount from start of the year till the end of selected month. ' +
			'Number of customers who contributed to sales.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		sales_trend_line_chart:
			'Trend of sales, profit and credit note based on selected timeframe. ' +
			'Data is always compared with the previous year.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		shared_sales_trend_column:
			'(All) - Sales from all customers. ' +
			'<br>(Existing) - Sales from existing customers. ' +
			'<br>(New) - Only includes sales from new customer in the acquired month.',
		agent_top_agent_table:
			'Top 5 sales agent and remaining agents based on selected date range.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.</span>',
		sales_top_customer_table:
			'Top 5 sales customer in the report month.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		shared_sales_analysis_tree_map:
			'Sales based on selected date range, grouped by Stock Item or Stock Group.' +
			'<br><br>% shows each item\'s share of the total.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',

		// =====================================================================
		// SALES - PROGRESS
		// =====================================================================
		sales_open_quotations_tracking_table:
			'Open quotation documents as of the selected date range. ' +
			'Only quotations within 60 days are valid in the table while the chart considers all quotations.',
		sales_quotation_conversion_rate_by_document_bar_chart:
			'Individual agent\'s sales conversion based on selected period, sorted by conversion rate in descending order.',
		sales_pending_sales_order_table:
			'Pending sales order documents as of the selected date range.',
		sales_order_conversion_rate_bar_chart:
			'Individual agent\'s sales outstanding based on selected period, sorted by outstanding rate in ascending order.',

		// =====================================================================
		// SALES - GROWTH
		// =====================================================================
		sales_active_customer_number_displayer:
			'Number of customers that made purchases based on selected year, ' +
			'including total sales and average sales per customer.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		sales_existing_customer_number_displayer:
			'Number of customers who previously made purchases and returned during the report year.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		sales_new_customer_number_displayer:
			'Number of new customers acquired within the report year.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		sales_new_customer_table:
			'New customers acquired in the selected month, based on selected year.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		sales_customer_no_activity_table:
			'Last sales transaction of each customer in selected month, based on selected year.' +
			'<br><br><span class="info-optional">Optional: Include Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',

		// =====================================================================
		// SALES - ANALYSIS
		// =====================================================================
		sales_total_quotation_number_displayer:
			'Number of quotation documents and total quotation amount.',
		sales_converted_quotation_number_displayer:
			'Number of quotations transferred partial/fully to sales invoice. Amount is transferred value only.',
		sales_open_quotation_number_displayer:
			'Value of quotations yet to transfer or cancel, with total amount.',
		sales_total_sales_by_region_bar_chart:
			'Total sales of each region.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		sales_total_sales_comparison_by_industry_bar_chart:
			'Total sales of each industry (Company Category) in comparison with last year.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		sales_total_sales_comparison_by_period_bar_chart:
			'Total sales of each month in comparison to the same period last year.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		sales_total_sales_comparison_by_region_bar_chart:
			'Total sales of each region in comparison with last year.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		sales_of_sales_contribution_by_region_bar_chart:
			'Percentage of sales by region to total year-to-month sales, compared with last year.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		sales_invoice_trend_line_chart:
			'Sales trend based on selected period.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note, Tax, Show Profit.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		sales_order_trend_line_chart: 'Sales order trend based on selected period.',
		sales_by_agents_bar_chart:
			'Sales amount by each sales agent with breakdown of top 5 item groups. Ranked by total sales amount.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note, Tax.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		sales_period_comparison_line_chart:
			'Comparing sales based on selected periods.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		agent_sales_metric_total_sales:
			'Total sales amount for the entire year. Number in bracket is the billing count. ' +
			'<br>E.g. RM 50,000 (20) means whole year sales are RM 50,000 from 20 invoices.',
		agent_sales_metric_average_sales_per_case:
			'Average sales value for each billing (case).',
		agent_sales_metric_average_sales_per_month: 'Average sales value per month.',
		agent_sales_metric_average_sales_case_per_month: 'Average number of billings per month.',
		agent_sales_metric_average_sales_cycle:
			'Average days taken to make a sale. <br>E.g. 20 days means a sale is made every 20 days.',
		agent_sales_metric_total_quotation:
			'Total quotation amount for the entire year. Number in bracket is the quotation count. ' +
			'<br>E.g. RM 100,000 (10) means whole year quoted value is RM 100,000 in 10 quotations.',
		agent_sales_metric_average_quotation_case_per_month:
			'Average number of quotations generated per month.',
		agent_sales_metric_win_rate:
			'Percentage of converted quotations against outstanding quotations. Cancelled quotations are excluded.',
		sales_metrics_table:
			'Year-over-year comparison of sales and quotation metrics.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note, Tax.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',

		// =====================================================================
		// SALES - DELIVERY PLANNING
		// =====================================================================
		sales_to_deliver:
			'List of orders that should be delivered, sorted by how far off they are from expected delivery day.' +
			'<br><br>OS Item represents the number of items that are overdue, not the total quantity overdue.',
		sales_fulfilled_sales_order:
			'List of orders that are fulfilled.' +
			'<br><br>If actual delivery date is invalid, the system treats it as 01/01/1970.' +
			'<br>Global date filter is based on SO "Expected Delivery".',

		// =====================================================================
		// AGENT - OVERVIEW
		// =====================================================================
		agent_12_months_sales_number_displayer:
			'Year to date sales for selected agent.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		agent_ytd_revenue_contribution_number_displayer:
			'Selected agent\'s year to date sales as percentage of company total.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		agent_ytd_sales_profit_number_displayer:
			'Year to date sales profit or margin for selected agent.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note, Toggle Profit/Margin.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		agent_sales_number_displayer:
			'Sales amount for selected agent in report month.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		agent_mtd_revenue_contribution_number_displayer:
			'Selected agent\'s sales as percentage of company total in report month.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		agent_sales_profit_number_displayer:
			'Sales profit or margin for selected agent in report month.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note, Toggle Profit/Margin.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		agent_sales_trend_line_chart:
			'Rolling 12-month trend of selected agent\'s sales, profit, and credit note, compared with previous year.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		agent_sales_analysis_tree_map:
			'Year to date sales for selected agent, grouped by Stock Item, Customer, Area, etc.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		agent_sales_metrics_table:
			'Year to date sales metrics for selected agent.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note, Tax.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		agent_salesMetrics_salesRank: 'Agent\'s sales ranking for each year.',
		agent_salesMetrics_totalSales: 'Agent\'s net sales amount for each year.',
		agent_salesMetrics_salesMargin: 'Agent\'s sales margin for each year.',
		agent_salesMetrics_revenueContribution:
			'Agent\'s sales as percentage of company total.',
		agent_salesMetrics_grossProfitContribution:
			'Agent\'s profit as percentage of company total profit.',
		agent_salesMetrics_totalCases:
			'Agent\'s number of sales cases (documents) for each year.',
		agent_salesMetrics_caseContribution:
			'Agent\'s cases as percentage of company total cases.',
		agent_salesMetrics_averageSalesPerCase:
			'Agent\'s average sales value per case (document).',
		agent_salesMetrics_averageSalesCasePerMonth:
			'Agent\'s average number of sales cases per month.',
		agent_salesMetrics_averageSalesPerMonth: 'Agent\'s average sales value per month.',
		agent_salesMetrics_averageSalesCycle:
			'Agent\'s average days between sales. E.g. 4 days means a sale every 4 days on average.',

		// =====================================================================
		// STOCK - OVERVIEW
		// =====================================================================
		stk_closing_stock_by_location: 'Closing stock by location based on selected date range.',
		stock_sales_tree_map:
			'Sales by stock group based on selected date range. View by amount or quantity.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.</span>',
		stock_purchase_tree_map:
			'Purchase by stock group based on selected date range. View by amount or quantity.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.</span>',
		stock_group_sales_purchase_bar_chart:
			'Sales vs purchase by stock group based on selected date range. View by amount or quantity.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.</span>',

		// =====================================================================
		// STOCK - ANALYSIS PAGE 1
		// =====================================================================
		stock_availability_table:
			'Available stock as of today. Reorder advice based on Reorder Level in SQL Account.' +
			'<br><br>Check dashboard Last Synced status for data freshness.',
		stock_batch_expiry_table:
			'Stock batch expiry status by location as of today.',

		// =====================================================================
		// STOCK - ANALYSIS PAGE 2
		// =====================================================================
		stock_item_movement_table:
			'Stock in/out movement based on selected date range.',
		stock_item_sales_by_customer_table:
			'Items sold to selected customer(s) based on selected date range.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.</span>',
		shared_stock_item_sales_by_customer_table:
			'Items sold to selected customer(s) based on selected date range.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.</span>',
		shared_stock_item_purchase_by_supplier_table:
			'Items purchased from selected supplier(s) based on selected date range.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.</span>',

		// =====================================================================
		// CUSTOMER - OVERVIEW
		// =====================================================================
		customer_total_outstanding_number_displayer:
			'Total customer outstanding amount (including customer deposit) as of the selected date.' +
			'<br><br><span class="info-optional">Optional: Include Unapplied (payments/credits not yet knocked off against invoices).</span>',
		customer_total_due_number_displayer:
			'Total customer due amount for invoice and debit note as of the selected date.' +
			'<br><br><span class="info-optional">Optional: Include Unapplied (payments/credits not yet knocked off against invoices).</span>',
		customer_overdue_number_displayer:
			'Percentage of overdue over total outstanding as of selected date.' +
			'<br><br><span class="info-optional">Optional: Include Unapplied (payments/credits not yet knocked off against invoices).</span>',
		customer_outstanding_tree_map:
			'Outstanding amount by customer as of selected date.' +
			'<br><br><span class="info-optional">Optional: Include Unapplied (payments/credits not yet knocked off against invoices).</span>',
		customer_overdue_tree_map:
			'Overdue amount by customer as of selected date.' +
			'<br><br><span class="info-optional">Optional: Include Unapplied (payments/credits not yet knocked off against invoices).</span>',
		customer_overdue_table:
			'Customer documents due within selected due period, as of selected date.',
		customer_un_applied_amount_pie_chart:
			'Unapplied amount by document type as of selected date.',
		customer_aging_summary_bar_chart:
			'Summary of customer aging by period as of selected date.' +
			'<br><br><span class="info-optional">Optional: Include Unapplied (payments/credits not yet knocked off against invoices).</span>',
		customer_aging_overview_table:
			'Customer aging as of selected date, aged by document date.' +
			'<br><br><span class="info-optional">Optional: Include Unapplied (payments/credits not yet knocked off against invoices).</span>',
		customer_due_aging_table:
			'Customer aging as of selected date, aged by due date.' +
			'<br><br><span class="info-optional">Optional: Include Unapplied (payments/credits not yet knocked off against invoices).</span>',

		// =====================================================================
		// CUSTOMER - ANALYSIS PAGE 1
		// =====================================================================
		customer_ytd_sales_number_displayer:
			'Year to date sales for selected customer. Ranking based on customers with sales in period.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		customer_ytd_gross_profit_number_displayer:
			'Year to date sales profit or margin for selected customer.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note, Toggle Profit/Margin.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		customer_cumulative_sales_number_displayer:
			'All-time trade value with selected customer. Average annual sales excludes current year.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		customer_mtd_sales_number_displayer:
			'Sales for selected customer in report month. Ranking based on customers with sales in period.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		customer_mtd_gross_profit_number_displayer:
			'Sales profit or margin for selected customer in report month.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note, Toggle Profit/Margin.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		customer_ytd_revenue_contribution_number_displayer:
			'Selected customer\'s year to date sales as percentage of company total.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		customer_sales_trend_line_chart:
			'Rolling 12-month sales trend for selected customer, compared with previous year.' +
			'<br><br>% shows each month\'s share of the year (bars only).' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		customer_sales_trend_column:
			'Sales amount, sales profit, or payment from this customer in respective months.',
		customer_sales_trend_line:
			'Line presents data in running total (accumulates monthly).',

		// =====================================================================
		// CUSTOMER - ANALYSIS PAGE 2
		// =====================================================================
		customer_analysis_tree_map:
			'Sales based on selected date range, grouped by Customer, Area, Stock Item, etc.' +
			'<br><br>% shows each item\'s share of the total.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Sales Module Only (SL) excludes direct Customer Module (AR) documents.</span>',
		customer_document_analysis_invoice_vs_payment_comparison_bar_chart:
			'Billing summary by document type based on selected date range.' +
			'<br><br>% shows each document type\'s share of the total across all customers/agents' +
			' — filter by customer or agent to see their contribution.',
		customer_payment_analysis_bar_chart:
			'Collection timeliness for customer invoices within selected year.' +
			'<br><br>On Time - Collected before due date.' +
			'<br>30 Days - Collected up to 30 days after due.' +
			'<br>> 30 Days - Collected more than 30 days after due.' +
			'<br><br>% shows each bucket\'s share of that month\'s collections.',

		// =====================================================================
		// PURCHASE - OVERVIEW
		// =====================================================================
		purchase_ytd_purchase_number_displayer:
			'Year to date purchase amount from start of the year till end of report month.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Purchase Module Only (PH) excludes direct Supplier Module (AP) documents.</span>',
		purchase_current_month_purchase_number_displayer:
			'Current month purchase amount based on selected month.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Purchase Module Only (PH) excludes direct Supplier Module (AP) documents.</span>',
		purchase_trend_bar_line_chart:
			'Rolling 12-month trend of purchase, credit note, and payment, compared with previous year.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Purchase Module Only (PH) excludes direct Supplier Module (AP) documents.</span>',

		// =====================================================================
		// PURCHASE - ANALYSIS
		// =====================================================================
		purchase_total_purchase_number_displayer:
			'Total purchase amount based on selected date range.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note (deducts Supplier Credit Note), Debit Note.' +
			'<br>Purchase Module Only (PH) excludes direct Supplier Module (AP) documents.</span>',
		purchase_total_credit_note_number_displayer:
			'Total Supplier Credit Note (purchase return) amount based on selected date range.' +
			'<br><br><span class="info-optional">Purchase Module Only (PH) excludes direct Supplier Module (AP) documents.</span>',
		purchase_total_purchase_order_number_displayer:
			'Purchase order amount based on selected date range.',
		purchase_analysis_tree_map:
			'Purchase based on selected date range, grouped by Stock Item or Stock Group.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Purchase Module Only (PH) excludes direct Supplier Module (AP) documents.</span>',
		purchase_period_comparison_line_chart:
			'Purchase comparison by selected period (31 days, 12 months, or 24 months).' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Purchase Module Only (PH) excludes direct Supplier Module (AP) documents.</span>',

		// =====================================================================
		// SUPPLIER - OVERVIEW
		// =====================================================================
		supplier_total_outstanding_number_displayer:
			'Total outstanding amount to suppliers as of selected date.' +
			'<br><br><span class="info-optional">Optional: Include Unapplied (payments/credits not yet knocked off against invoices).</span>',
		supplier_total_due_number_displayer:
			'Total overdue amount to suppliers as of selected date.' +
			'<br><br><span class="info-optional">Optional: Include Unapplied (payments/credits not yet knocked off against invoices).</span>',
		supplier_overdue_number_displayer:
			'Percentage of overdue over total outstanding as of selected date.' +
			'<br><br><span class="info-optional">Optional: Include Unapplied (payments/credits not yet knocked off against invoices).</span>',
		supplier_outstanding_tree_map:
			'Outstanding amount by supplier as of selected date.' +
			'<br><br><span class="info-optional">Optional: Include Unapplied (payments/credits not yet knocked off against invoices).</span>',
		supplier_upcoming_due_table:
			'Supplier documents due within selected due period, as of selected date.',
		supplier_un_applied_amount_pie_chart: 'Unapplied amount by document type as of selected date.',
		supplier_aging_overview_table:
			'Supplier aging as of selected date, aged by document date.' +
			'<br><br><span class="info-optional">Optional: Include Unapplied (payments/credits not yet knocked off against invoices).</span>',
		supplier_aging_summary_bar_chart:
			'Summary of supplier aging by period as of selected date.' +
			'<br><br><span class="info-optional">Optional: Include Unapplied (payments/credits not yet knocked off against invoices).</span>',

		// =====================================================================
		// SUPPLIER - ANALYSIS
		// =====================================================================
		supplier_analysis_tree_map:
			'Purchase based on selected date range, grouped by Supplier, Stock Item, Stock Group, or Supplier Size.' +
			'<br><br>% shows each item\'s share of the total.' +
			'<br><br><span class="info-optional">Optional: Include Credit Note, Debit Note.' +
			'<br>Purchase Module Only (PH) excludes direct Supplier Module (AP) documents.</span>',
		supplier_document_analysis_invoice_vs_payment_comparison_bar_chart:
			'Billing summary by document type based on selected date range.' +
			'<br><br>% shows each document type\'s share of the total across all suppliers/agents' +
			' — filter by supplier or agent to see their contribution.',
		supplier_payment_analysis_bar_chart:
			'Payment timeliness for supplier invoices within selected year.' +
			'<br><br>On Time - Paid within due month.' +
			'<br>30 Days - Paid up to 30 days after due.' +
			'<br>> 30 Days - Paid more than 30 days after due.' +
			'<br><br>% shows each bucket\'s share of that month\'s payments.',


		// =====================================================================
		// BUSINESS PERFORMANCE - P&L
		// =====================================================================
		bp_revenue_number_displayer: 'Net revenue based on selected account period.',
		bp_cogs_number_displayer: 'Cost of goods sold based on selected account period.',
		bp_gross_profit_number_displayer: 'Net Revenue minus COGS based on selected account period.',
		bp_other_income_extra_ordinary_income_number_displayer: 'Other Income and Extra Ordinary Income based on selected account period.',
		bp_expenses_number_displayer: 'Total expenses based on selected account period.',
		bp_net_profit_before_tax_number_displayer: 'Gross Profit plus Other Income minus Expenses based on selected account period.',
		bp_profit_loss_bar_chart: 'Profit/Loss breakdown based on selected date range.',
		bp_revenue_purchases_expenses_bar_chart: 'Rolling 12-month overview of revenue, purchase, and expenses.',
		bp_profit_loss_monthly_comparison_comparison: 'Monthly P&L breakdown with expandable details for Revenue, COGS, Other Income, Expenses, and Net Profit.',
		bp_expenses_table: 'Expenses breakdown based on selected date range.',
		bp_cost_of_goods_sold_table:
			'COGS breakdown based on selected date range.' +
			'<br>MC = Manufacturing Cost.',

		// =====================================================================
		// BUSINESS PERFORMANCE - BALANCE SHEET
		// =====================================================================
		bp_total_assets_number_displayer: 'Total assets as of selected date.',
		bp_total_liabilities_number_displayer: 'Total liabilities as of selected date.',
		bp_total_equity_number_displayer: 'Total equities as of selected date.',
		bp_financial_position_assets_chart: 'Assets breakdown as of selected date.',
		bp_financial_position_liabilities_equities_chart: 'Liabilities & Equities breakdown as of selected date.',

		// =====================================================================
		// BUSINESS PERFORMANCE - CASH FLOW
		// =====================================================================
		bp_cash_on_hand_number_displayer: 'Total cash on hand as of selected date.',
		bp_cash_in_bank_number_displayer: 'Total cash in bank as of selected date.',
		bp_cash_flow_bar_chart:
			'Rolling 12-month cash level and cash flow.' +
			'<br>Cash Level = Cash + Bank as of month end.' +
			'<br>Cash Flow = Monthly net inflow/outflow.',
		bp_cash_out_flow_table: 'Cash out transactions based on selected date range.',
		bp_cash_in_flow_table: 'Cash in transactions based on selected date range.',

		// =====================================================================
		// BUSINESS PERFORMANCE - FINANCIAL RATIO
		// =====================================================================
		bp_financial_performance_table:
			'Financial ratios for selected year compared with previous 4 years.',
		bp_financial_revenueGrowth:
			'Net revenue growth compared to previous year.' +
			'<br>Formula: (Current - Previous) / Previous',
		bp_financial_grossMargin:
			'Gross profit as percentage of net revenue.' +
			'<br>Formula: Gross Profit / Net Revenue',
		bp_financial_netMargin:
			'Net profit as percentage of net revenue.' +
			'<br>Formula: Net Profit / Net Revenue',
		bp_financial_efficiencyRatio:
			'Expenses as percentage of net revenue.' +
			'<br>Formula: Expenses / Net Revenue',
		bp_financial_cashRatio: 'Formula: (Cash + Bank) / Current Liabilities',
		bp_financial_currentRatio: 'Formula: Current Assets / Current Liabilities',
		bp_financial_debtRatio: 'Formula: Total Liabilities / Total Assets',
		bp_financial_returnOnEquity: 'Formula: Net Profit / Total Equity',
		bp_financial_leverageRatio: 'Formula: Total Liabilities / Total Equity',
		bp_financial_fixedAssetsToNetWorthRatio:
			'Formula: Fixed Assets / (Total Assets - Total Liabilities)',
		bp_financial_currentLiabilitiesToNetWorthRatio:
			'Formula: Current Liabilities / (Total Assets - Total Liabilities)',
		bp_financial_workingCapitalToTotalAssets:
			'Formula: (Current Assets - Current Liabilities) / Total Assets',
		bp_financial_inventoryTurnover: 'Formula: COGS / Average Inventory',
		bp_financial_totalAssetTurnover: 'Formula: Net Revenue / Average Total Assets',
		bp_financial_accountReceivableTurnover: 'Formula: Net Revenue / Account Receivable',
		bp_financial_accountPayableTurnover: 'Formula: Net Revenue / Account Payable',
		bp_cash_bank_ratio_displayer:
			'Monthly cash ratio based on selected date range.' +
			'<br>Formula: (Cash + Bank) / Current Liabilities',
		bp_current_ratio_displayer:
			'Monthly current ratio based on selected date range.' +
			'<br>Formula: Current Assets / Current Liabilities',
		bp_liabilities_ratio_displayer:
			'Monthly debt ratio based on selected date range.' +
			'<br>Formula: Total Liabilities / Total Assets',
		bp_working_capital_to_total_assets_displayer:
			'Monthly working capital to total assets based on selected date range.' +
			'<br>Formula: (Current Assets - Current Liabilities) / Total Assets',
		bp_fixed_assets_to_net_worth_ratio_displayer:
			'Monthly fixed assets to net worth ratio based on selected date range.' +
			'<br>Formula: Fixed Assets / (Total Assets - Total Liabilities)',
		bp_current_liabilities_to_net_worth_ratio_displayer:
			'Monthly current liabilities to net worth ratio based on selected date range.' +
			'<br>Formula: Current Liabilities / (Total Assets - Total Liabilities)',

		// =====================================================================
		// FORECAST
		// =====================================================================
		forecast_compare_line_chart:
			'Forecast vs actual sales and net profit comparison for selected year.',
		forecast_compare_table:
			'Forecast vs actual comparison for sales, gross profit, expenses, and net profit.',

		// =====================================================================
		// PROJECT TRACKER
		// =====================================================================
		project_general: 'Project value and progress overview for selected project.',
		project_billing_pie_chart: 'Billing breakdown by document type for selected project.',
		project_summary_bar_chart:
			'Project financial summary. View by Current Month, This Year, or Total.',

		// =====================================================================
		// CUSTOM MODULES - INSTAFRESH
		// =====================================================================
		cm_instafresh_daily_sales_bar:
			'Total sales up to selected date for each location.',
		cm_instafresh_daily_sales_table:
			'Individual daily sales according to date.',

		// =====================================================================
		// CUSTOM MODULES - NONSTOPAUTO
		// =====================================================================
		cm_nonstopauto_maintain_stock_item:
			'Stock item list with barcode, balance quantity, reference cost, and reference price as of selected date.',

		// =====================================================================
		// CUSTOM MODULES - WLS
		// =====================================================================
		cm_wls_credit_limit_analysis:
			'% Late = Late On Due Invoice &divide; Due Invoice &times; 100. ' +
			'An invoice is counted as late when it was fully paid (knocked off) after its due date, or remains outstanding after its due date.' +
			'<br><br>Tier is based on % Late: ' +
			'<br> A = &le;10% late ' +
			'<br> B = >10% to &le;20% late ' +
			'<br> C = >20% late' +
			'<br><br>Recommended Limit = TTM Average Monthly Sales &times; tier multiplier, rounded to the nearest 100: ' +
			'<br> A &times; 8 ' +
			'<br> B &times; 4 ' +
			'<br> C &times; 2',
		cm_wls_reorder_analysis_manual:
			'Manual reorder analysis based on selected date range. Calculates daily demand, reorder level, and recommended order quantity.' +
			'<br><br><span class="info-optional">Optional: Include Inactive & Discontinued Items, Update List, Additional Info.</span>',
		cm_wls_reorder_analysis_system:
			'System reorder analysis based on selected date. Calculates daily demand using lead time period, inventory days, and recommended order quantity.' +
			'<br><br><span class="info-optional">Optional: Include Inactive & Discontinued Items, Update List, Additional Info.</span>',
		cm_wls_customer_outstanding_summary:
			'Customer outstanding summary by aging period ' +
			'(Current, 0-30, 31-60, 61-90, 91-120, >120 days) as of selected date.' +
			'<br>Shows Outstanding, PDC, and Outstanding after PDC.' +
			'<br><br><span class="info-optional">Optional: Exclude Current Amount.</span>',
		cm_wls_customer_outstanding_promo:
			'Customer outstanding details for Promo invoices only, by aging period as of selected date.' +
			'<br><br><span class="info-optional">Optional: Apply PDC, Credit Term, Due Details.</span>',
		cm_wls_customer_outstanding_nonpromo:
			'Customer outstanding details for Non-Promo invoices only, by aging period as of selected date.' +
			'<br><br><span class="info-optional">Optional: Apply PDC, Credit Term, Due Details.</span>',
		cm_wls_customer_outstanding_all:
			'Customer outstanding details for all invoices, by aging period as of selected date.' +
			'<br><br><span class="info-optional">Optional: Apply PDC, Credit Term, Due Details.</span>',

		// =====================================================================
		// CUSTOM MODULES - VIVALEC
		// =====================================================================
		cm_vivalec_closing_stock_by_group: 'Expandable closing stock groups.',
		cm_vivalec_stock_item_budgeting:
			'Shows stock item budgeting status according to available budget. ' +
			'<br>> 0 = Available Budget to Order. ' +
			'<br>< 0 = No Available Budget To Order. ' +
			'<br>Else = None',

		// =====================================================================
		// CUSTOM MODULES - PALMKING
		// =====================================================================
		cm_palmking_monthly_sales_table:
			'Datatable showing sales, cost, margin, and margin percentage of each month.',
		cm_palmking_monthly_sales_chart:
			'Monthly Sales chart separated by overseas and local.',
		cm_palmking_monthly_sales_company:
			'Datatable showing sales sorted by company.,',

		// =====================================================================
		// CUSTOM MODULES - TIARCO
		// =====================================================================
		cm_tiarco_yearly_same_period:
			'A comparison chart using data from the previous year, and this year month-to-date to the month chosen.',
		cm_tiarco_yearly_same_period_table:
			'A comparison table using data from the previous year, and this year month-to-date to the month chosen.',
		cm_tiarco_monthly_comparison:
			'A month-to-date comparison chart data from this year up to the end of the month chosen.',
		cm_tiarco_monthly_comparison_table:
			'A month-to-date comparison table using data from this year to the end of the month chosen.',
		cm_tiarco_other_income_breakdown:
			'A descriptive breakdown of what is included in Other Income.',
		cm_tiarco_yearly_sales_analysis:
			'Each customer purchased item in total value or qty. <br> Credit note & Debit note is optional to be included.',

		// =====================================================================
		// CUSTOM MODULES - STARFIELD
		// =====================================================================
		cm_starfield_budget_variance:
			'Budget variance report comparing actual spend vs budget for OPEX and CAPEX.' +
			'<br><br>Legend: Red = Spent > 80%, Orange = Spent > 50%.',
		cm_starfield_gl_trans_report:
			'GL transaction details based on selected date range.',

		// =====================================================================
		// CUSTOM MODULES - EVERGREEN
		// =====================================================================
		cm_evergreen_stock_availability:
			'Stock availability with incoming quantity from pending purchase orders. Filter by brand, GSM, and size.' +
			'<br><br>An item is listed only if <b>Avail. Qty &gt; 0 or Incoming Qty &gt; 0</b>. ' +
			'Items with nothing on hand <i>and</i> nothing incoming are hidden by design, ' +
			'so an item missing from this table is out of stock with no open purchase order. ' +
			'<br>Note that negative and blank quantities are treated the same as zero.',

		// =====================================================================
		// CUSTOM MODULES - NYCX
		// =====================================================================
		cm_nycx_discount_table_item: 'Discount table based on item-code selected.',
		cm_nycx_discount_table_all: 'Discount table for all items',

		// =====================================================================
		// CUSTOM MODULES - KIM CHOK
		// =====================================================================
		cm_kimchok_sales_by_location:
			'Compares sales amount across the selected Locations (branches), one line per location. ' +
			'Date Range shows a point per month over the full selected range; ' +
			'7 Days shows the 7 days ending at "Date To".' +
			'<br>Amount is in home currency, before tax. ' +
			'Sales Invoice, Cash Sales and Debit Note are added; Credit Note is subtracted.' +
			'<br><br><span class="info-optional">Optional: Sales Invoice, Cash Sales, Credit Note, Debit Note, Tax, Show Values.</span>',
		cm_kimchok_sales_by_stock_group:
			'Compares sales amount across the selected Stock Groups, one line per stock group. ' +
			'Stock Group is the SQL Account system stock-group field.' +
			'<br>Date Range shows a point per month over the full selected range; ' +
			'7 Days shows the 7 days ending at "Date To".' +
			'<br>Amount is in home currency, before tax. ' +
			'Sales Invoice, Cash Sales and Debit Note are added; Credit Note is subtracted.' +
			'<br><br><span class="info-optional">Optional: Sales Invoice, Cash Sales, Credit Note, Debit Note, Tax, Show Values.</span>',
		cm_kimchok_sales_by_group:
			'Compares sales amount across the selected Groups, one line per group. ' +
			'Group is a merged category above stock-group level (SQL Account UDF field).' +
			'<br>Date Range shows a point per month over the full selected range; ' +
			'7 Days shows the 7 days ending at "Date To".' +
			'<br>Amount is in home currency, before tax. ' +
			'Sales Invoice, Cash Sales and Debit Note are added; Credit Note is subtracted.' +
			'<br><br><span class="info-optional">Optional: Sales Invoice, Cash Sales, Credit Note, Debit Note, Tax, Show Values.</span>',
		cm_kimchok_sales_by_location_pie:
			'Each Location\'s (branch\'s) share of total sales, one slice per location. ' +
			'Shares the charts\' Date Range / 7 Days toggle (Date Range = the full selected range; ' +
			'7 Days = the 7 days ending at "Date To").' +
			'<br>Amount is in home currency, before tax. ' +
			'Sales Invoice, Cash Sales and Debit Note are added; Credit Note is subtracted. ' +
			'Only locations with a positive net are shown.' +
			'<br><br><span class="info-optional">Optional: Sales Invoice, Cash Sales, Credit Note, Debit Note, Tax, Show Values.</span>',

		// =====================================================================
		// CUSTOM MODULES - DATAEMP
		// =====================================================================
		cm_dataemp_sales:
			'Total net revenue (sales + sales adjustments) for the selected year & department. Progress shows achievement against budget target.',
		cm_dataemp_cost:
			'Total cost of goods sold for the selected year & department. The ratio shows cost as a percentage of net revenue.',
		cm_dataemp_gp:
			'Net Revenue minus Cost of goods sold for the selected year & department. The ratio shows gross profit margin as a percentage of net revenue.',
		cm_dataemp_dept_achievement:
			'Target vs Actual performance breakdown for the selected year & department. Toggle between Net Revenue, Cost, and Gross Profit views.',
		cm_dataemp_cumulative_chart:
			'Month-over-month cumulative trend comparing actual performance against target for the selected year & department. Toggle between Net Revenue, Cost, and Gross Profit views.',
		cm_dataemp_revenue_distribution:
			'Proportion of total revenue contributed for the selected year & department.',

		// =====================================================================
		// UNUSED / LEGACY ENTRIES (commented out)
		// =====================================================================
		// Agent - unused:
		// agent_activeCustomer:
		// 	'Number of customers this agent had sold to in the past 12 months from report date.',
		// Stock - unused:
		// sg_salesHistory:
		// 	'Sales performance of stock groups within selected period.',
		// sg_purchaseHistory:
		// 	'Purchase history of stock groups within selected period.',
		// sg_profitByStockGroup:
		// 	'Sales profit and profit margin for each stock group within selected period
		// 	<br> Profit calculation based on Item. Ref Cost in SQL account.',
		// sg_itemSalesBreakdown: 'Breakdown of sales amount by stock item',
		// sg_bestSellingStockItem: 'Top 20 best selling stock item by amount',
		// stk_profitByStockItem:
		// 	'Estimated sales profit and profit margin for each stock item.
		// 	Profit calculation based on Item. <br> Tax is optional in the calculation.',
		// stk_stockAging:
		// 	'Stock aging by amount or by quantity. <br> Aging based on first in first out method.',
		// stk_salesPurchaseMovement:
		// 	'Average Sales and Purchase price for a stock item. <br> With transaction volume in each month.',
		// stk_listOfBuyers: 'List of company who bought this item in selected year.',
		// stk_metric: 'List of company who bought this item in selected year.',
		// stk_metric_totalSales:
		// 	'Total sales value and quantity of the product within selected year.',
		// stk_metric_stockGroupSalesPercentage:
		// 	'Sales contribution (by value) of this product within same stock group.',
		// stk_metric_productSalesPercentage:
		// 	'Sales contribution (by value) of this product in overall product sales.',
		// stk_metric_averageUnitDeal: 'Average selling unit per transaction',
		// stk_metric_averageUnitMonth: 'Average selling unit per month.',
		// stk_metric_bestMonth:
		// 	'Highest monthly sales (by value) within selected year.
		// 	<br> If there are more than 1 month with same value, the earliest month will display.',
		// stk_metric_lowestMonth:
		// 	'Lowest monthly sales (by value) within selected year.
		// 	<br> Report only covers months that have sales.
		// 	<br> If there are more than 1 month with same value, the latest month will display.',
		// Customer - unused:
		// cst_customerLocations:
		// 	'Location of customer based on coordinates saved in SQL Account.',
		// cst_averageValuePerTransaction: 'Average billing value',
		// cst_averageUnitPerTransaction: 'Average unit in each billing',
		// cst_tradeOverRevenue:
		// 	'Percentage of customer net sales amount over total net sales amount in PnL.
		// 	<br> (PnL net sales amount include non-customer sales)',
		// cst_periodCollection: 'Collection status for AR due in selected period.',
		// cst_paymentPunctuality:
		// 	'This chart shows the punctuality of customer making payments. <br><br> Legend:
		// 	<br> On-Time - Number of documents that fully paid before due date.
		// 	<br> On-Buffer - Number of documents that fully paid within 1 week after due date.
		// 	<br> Late - Number of documents that fully paid 2 week after due date but no longer than 5 weeks.
		// 	<br> Very Late - Number of documents that fully paid more than 5 weeks after due date.
		// 	<br> <br> eg. If you have On-Time: 150 (75.32%) means that there are 150 invoices payments
		// 	were collected on time, representing 75.32% of all documents that had completely paid.
		// 	<br> *Documents that are still outstanding is excluded.',
		// Supplier - unused:
		// sup_supplierLocations:
		// 	'Location of supplier based on coordinates saved in SQL Account.',
		// sup_averageValuePerTransaction:
		// 	'Average purchase value for each transaction (document).',
		// sup_averageUnitPerTransaction:
		// 	'Avarage purchase unit in each transaction (document).',
		// sup_tradeOverRevenue:
		// 	'Percentage of net purchase amount of selected supplier over total net purchase amount in PnL.',
		// sup_purchaseComparison: 'Comparing net purchase in selected period.',
		// sup_periodDuePayment: 'Payment status for AP due in selected period.',
		// Business Performance - unused:
		// gl_topDebtors: 'Highest outstanding debtors',
		// gl_topCreditors: 'Highest outstanding creditors',
		// bp_salesTracker:
		// 	'Accumulated sales amount based on Cash Sales & Sales Invoice',
		// bp_assetsTurnover:
		// 	'Net Sales / Total Assets <br> Current month ratio is up to current day.',
		// bp_debtToEquityRatio:
		// 	'Total Debt / Total Equity <br> Current month ratio is up to current day.',
		// bp_fixedAssetsTurnoverRatio:
		// 	'Net Sales / [ ( Net Fixed Assets beginning balance - ending balance ) / 2 ]
		// 	<br> Current month ratio is up to current day.',
		// bp_receivableTurnover:
		// 	'Annual Credit Sales / Accounts Receivable
		// 	<br> Current month ratio is up to current day.',
		// bp_averageCollectionPeriod:
		// 	'Accounts Receivable / ( Annual Credit Sales / 365days )
		// 	<br> Current month ratio is up to current day.',
		// Comparison page (cp_item, cp_category, cp_value) - page does not exist
		// Dashboard - NewSummaries.vue is not rendered:
		// dsh_stockGroupPerformance:
		// 	'Stock group sales performance for 7 days trailing.',
		// dsh_todayCustomerPayment: 'Customer payment receive today.',
		// dsh_todaySupplierPayment: 'Today payment to supplier.',
		// Sales - unused:
		// sl_agentOustandingRatio:
		// 	'Individual agent\'s sales that are outstanding based on selected period',
		// sl_agentQuotationOutstandingnRate:
		// 	'Individual agent\'s sales outstanding based on selected period
		// 	and sort by outstanding rate in ascending order.',
		// Sales Growth - commented out in SalesGrowth.vue template:
		// sr_growth_customerGrowth:
		// 	'Yearly comparison of sales breakdown by new or existing customers.',
		// sr_growth_customerAnalysis:
		// 	'Year to date customer sales presented with different grouping options.',
		// sl_existingCustomer:
		// 	'Number of returned customers who previously made purchases based on selected year.
		// 	<br> Credit note is optional in the calculation.',
		// sl_newCustomer:
		// 	'Number of new customers acquired based on selected year.
		// 	<br> Credit note is optional in the calculation.',
		// Sales Overview - unused:
		// sl_monthTopCustomers:
		// 	'Top 5 customers and remaining amounts based on selected date range.
		// 	<br> Credit note is optional in the calculation.',
		// sr_summary_ytdSales:
		// 	'Year to date sales amount.  <br> From start of the year till the end of report month.',
		// sr_summary_salesTrend:
		// 	'Monthly sales trend based on Cash Sales & Sales Invoice.
		// 	<br> Note: Non item sales (Customer Invoice) is not included.',
		// sr_summary_salesTrend_line:
		// 	'Line presents data in running total (accumulates monthly).',
		// Sales Report - Customer unused:
		// sr_customer_paymentPunctuality:
		// 	'This chart shows the punctuality of customer making payments within report year.<br>Legend: ...',
		// sr_customer_agingSummary: 'Aging summary in the past 3 months.',
		// sr_customer_itemSalesAnalysis:
		// 	'Item sales in different period presented with different grouping options.
		// 	<br>Note: Non item sales (Customer Invoice) is not included.',
		// Sales Analysis unused: sl_topSalesMonthly, sl_topSalesQuarterly,
		//   sl_topSalesYearly, sl_agentOutstandingQuotation,
		//   sl_personalAgentRankingReport, sl_personalAgentTopStockGroupSales,
		//   sl_personalAgentTopFiveOutstanding
	};
}
